// The pure, Pixi-free damage-flash classifier and presentation tracker
// (`combat-strikes-and-damage-flash`, design E4/E5/E9; `client-rendering` "Damage-flash
// classification from an already-transmitted health fraction" / "The damage flash resets on
// every presentation discontinuity and ages against the playhead"). Mirrors `CorpseTracker`'s
// shape exactly: `onCloseFrame` once per accepted close frame, `advance` once per RAF frame,
// unit-testable without a GPU (D-086) — the renderer's own paint stays device-exercised only.
//
// **What this derives, and from what.** Combat produces a discrete per-second strike (D1 of
// `plan.md`), but the wire carries no per-entity "was struck" bit and no attacker-side cue (a
// deliberate non-goal — see design.md's Non-Goals). All the client has is a close-tier bee's
// own 0..255 health fraction (`BeeRecord.health`, already own-max-relative) and a cell's own
// 0..31 hp bucket (`CellRecord.hpBucket`, group 8). This module turns a DROP in either value,
// observed between two accepted close frames, into a flash — nothing more is on the wire to
// derive it from.
//
// **Why the bee rule needs a rate discriminator and the cell rule does not.** The only two
// things that reduce a bee's health are combat damage (an impulse, once per
// `attack_interval_s`) and the starvation drain (a constant rate) — and the wire carries no
// `food`, so there is nothing to attribute a drop to directly. The classifier instead compares
// the drop against the elapsed ticks since that entity's last observed sample: a real strike is
// large relative to any short window, the drain is small relative to the same window, and the
// separation is derived below rather than chosen by eye. `cell.hp` has only one writer that
// ever LOWERS it — razing (the other, `repair_cells`, only raises and clamps) — so no rate
// argument is needed for cells: any decrease is damage, full stop.
//
// **The indication is deliberately a LOWER BOUND: it may miss a real hit, it must never invent
// one.** Recorded limitations (`docs/dev/deferred.md`): two strikes inside one sample window
// merge into a single flash; in-hive regen inside a window can mask a strike; a starving bee's
// genuinely hostile damage is discarded by the same rule that excludes the drain; a window
// beyond the ceiling is withheld as "unknown" rather than guessed at; a low-resolution victim's
// (the queen's) single strike can fail to clear the noise floor and is withheld; and,
// structurally, THE KILLING BLOW NEVER FLASHES — a bee killed by the strike that removed it is
// simply absent from the next accepted frame, and there is no health sample to diff against.
// Nothing here tries to infer that case; a vanished id is just not classified.

import { Tier } from '../net/wire/messages';
import { ticksPer } from './tick-window';

// ---------------------------------------------------------------------------------------------
// The classifier's derived constants (design E5). Every number below is DERIVED from balance
// values, not chosen by eye — the derivation is written out so a preset move that invalidates
// the separation is visible in the arithmetic, not just in a magic number. See design.md E5 for
// the full worked table; the summary:
//
//   | victim        | max health | one strike (5 hp) | starvation drain/tick (15 Hz) |
//   |---------------|-----------:|-------------------:|------------------------------:|
//   | unbuffed bee  |        100 |          12.75 u    |                        0.34 u  |
//   | ×2-health bee |        200 |           6.375 u   |                        0.17 u  |
//   | queen (×5)    |        500 |           2.55 u    |                        0.068 u |
//
// The ratio strike:drain is FIXED (both divide by the same victim max, so it cancels), which is
// why the rule needs no balance value the client does not have. What is NOT fixed is
// quantization RESOLUTION: the larger the victim's max health, the fewer 0..255 units a strike
// resolves into — the unbuffed bee sets the false-positive bound (fastest fractional drain), the
// queen sets the sensitivity floor (coarsest strike).
// ---------------------------------------------------------------------------------------------

/**
 * The fastest fractional starvation drain, in wire units per SECOND —
 * `255 × starvation_drain_hp_per_s ÷ max_health` at the unbuffed bee's max health (the fastest
 * fractional case, since a health buff only slows the fraction's fall): `255 × 2 ÷ 100 = 5.1`.
 *
 * Deliberately a rate **per second**, carrying **no** tick rate (tick-rate-single-source /
 * design E4). A `STARVE_UNITS_PER_TICK` constant would silently bake in 15 Hz: the drain is a
 * rate per second, so its per-tick share changes with the tick rate, and a preset running at a
 * different rate would shift the false-positive bound with nothing failing. The comparison this
 * constant feeds is therefore always made in seconds (`elapsed_seconds = elapsed_ticks ÷
 * tickRateHz`), so the no-false-positive bound holds identically at any tick rate.
 */
export const STARVE_UNITS_PER_SECOND = 5.1;

/**
 * The worst-case quantization error of differencing two independently `round_ties_even`-rounded
 * 0..255 fractions: each sample can be off by up to 0.5 units from its true continuous value, so
 * their difference can be off by up to 1 unit in either direction. Added to the starvation bound
 * so the "no false positives" guarantee holds even at the worst phase of rounding, not just on
 * average.
 */
export const QUANT_SLACK_UNITS = 1;

/**
 * One unit above the largest value quantization noise alone can produce (`QUANT_SLACK_UNITS`) —
 * the floor below which a measured drop is never even considered, independent of the starvation
 * comparison. Strictly a floor: the starvation bound (which grows with the window) is usually
 * the binding constraint at anything but the shortest windows.
 */
export const MIN_DROP_UNITS = 2;

/**
 * The ceiling, held as a DURATION (milliseconds — never a raw tick count, per
 * tick-rate-single-source / design E4/E9), past which the elapsed-tick window is classified
 * **unknown** rather than compared at all. The impulse-vs-rate separation only holds while the
 * window is short relative to `attack_interval_s` (1 s): at 533 ms the required drop (4 u, at
 * elapsed = 8 ticks / 15 Hz) is still well under a single unbuffed strike (12.75 u), but much
 * beyond that a window can contain a whole strike's worth of drain and no single threshold would
 * serve both regimes. 533 ms is also ≈5× the nominal ~10 Hz close cadence, so ordinary frame
 * jitter never trips it — only a genuine discontinuity (a keyframe-dirty resync, a viewport pan
 * evicting/re-admitting a bee through `close_lod_cap` truncation churn, a cell leaving and
 * re-entering the cell cap, or a rounds-mode fast scrub) does. Converted to ticks via
 * {@link ticksPer}, exactly like the flash's own presentation window below — a duration must
 * never be read as a hardcoded tick count that silently assumes 15 Hz.
 */
export const MAX_ELAPSED_MS = 533;

/**
 * The flash's presentation window (ms): bounded ABOVE by the strike interval
 * (`attack_interval_s` = 1000 ms) — much beyond this, successive strikes fuse into a constant
 * tint and stop reading as discrete events, which is the whole point of the change (design E7) —
 * and BELOW by ordinary frame delivery, so a flash is not shorter than the gap between accepted
 * frames. 260 ms is 4 ticks at the shipped 15 Hz rate.
 */
export const DEFAULT_FLASH_MS = 260;

/** The minimal per-bee sample the classifier needs: id and the raw 0..255 wire health fraction
 * (`BeeRecord.health`, already relative to this bee's own — possibly buffed — max health). */
export interface BeeFlashSample {
  readonly id: number;
  readonly health: number;
}

/** The minimal per-cell sample the classifier needs: id and the raw 0..31 wire hp bucket
 * (`CellRecord.hpBucket`, group 8). */
export interface CellFlashSample {
  readonly id: number;
  readonly hpBucket: number;
}

/**
 * The four discontinuity tokens the tracker resets on (design "The damage flash resets on every
 * presentation discontinuity…"), mirroring `PresentationClock.PresentationTokens` plus an
 * explicit `connectionEpoch` (design requires it as its OWN pinned reset, distinct from
 * `streamEpoch`, unlike `PresentationClock` which folds a reconnect into the same `streamEpoch`
 * bump). `tier` is load-bearing here and is deliberately NOT part of `CorpseTracker`'s epoch
 * set: the corpse heuristic is about *disappearance*, this tracker is a *value diff*, and
 * per-bee health only exists at the close tier — without a tier reset, a zoom out to wide and
 * back in would compare health across an arbitrary gap.
 */
export interface DamageFlashTokens {
  readonly tier: Tier;
  readonly connectionEpoch: number;
  readonly streamEpoch: number;
  /** The replay playhead tick, or `null` in live play. A non-contiguous move — backwards by any
   * amount, or forwards by more than the classifier's elapsed-tick ceiling — is a
   * discontinuity. */
  readonly playhead: number | null;
}

/** One active flash: which entity, which namespace (bees and cells are distinct id spaces —
 * connection.ts's own discipline), and its current presentation intensity (0..1, modelled on
 * `corpseAlpha`). */
export interface DamageFlash {
  readonly kind: 'bee' | 'cell';
  readonly id: number;
  readonly intensity: number;
}

export interface DamageFlashTrackerOptions {
  /**
   * The running sim tick rate (Hz) — **REQUIRED**, sourced from `Welcome.tick_rate_hz` /
   * `manifest.tickRateHz` (tick-rate-single-source / design E4, exactly `CorpseTracker`'s own
   * contract): no defaulted argument, no `?? 15`, and no import of the generated
   * `SIM_TICK_RATE_HZ` mirror. A caller with no rate to pass must fail to compile rather than
   * silently deriving windows — and comparing the starvation bound — against a build default
   * that may not match the rate a replay's playhead is actually ticking at.
   */
  readonly tickRateHz: number;
  /** The flash presentation window, in ms (default `DEFAULT_FLASH_MS`). */
  flashMs?: number;
  /** The classifier's elapsed-tick ceiling, in ms (default `MAX_ELAPSED_MS`). */
  maxElapsedMs?: number;
  /** The flash presentation window, in ticks. Defaults to `ticksPer(flashMs, tickRateHz)`. */
  flashTicks?: number;
  /** The classifier's elapsed-tick ceiling, in ticks. Defaults to
   * `ticksPer(maxElapsedMs, tickRateHz)`. */
  maxElapsedTicks?: number;
}

interface LastSeenBee {
  readonly tick: number;
  readonly health: number;
}

interface LastSeenCell {
  readonly tick: number;
  readonly hpBucket: number;
}

interface ActiveFlash {
  elapsedMs: number;
  /** The playhead tick the drop was classified on, or `null` when classified in live play (no
   * playhead). Non-null selects the tick-anchored decay over the wall-clock one — exactly
   * `CorpseTracker.ActiveCorpse.spawnTick`'s contract. */
  readonly spawnTick: number | null;
}

export class DamageFlashTracker {
  private readonly tickRateHz: number;
  private readonly flashMs: number;
  private readonly flashTicks: number;
  private readonly maxElapsedTicks: number;

  private lastSeenBees = new Map<number, LastSeenBee>();
  private lastSeenCells = new Map<number, LastSeenCell>();
  private readonly activeBees = new Map<number, ActiveFlash>();
  private readonly activeCells = new Map<number, ActiveFlash>();

  private lastTier: Tier | null = null;
  private lastConnectionEpoch = -1;
  private lastStreamEpoch = -1;
  private lastPlayhead: number | null = null;
  private initialized = false;

  constructor(options: DamageFlashTrackerOptions) {
    this.tickRateHz = options.tickRateHz;
    this.flashMs = options.flashMs ?? DEFAULT_FLASH_MS;
    const maxElapsedMs = options.maxElapsedMs ?? MAX_ELAPSED_MS;
    this.flashTicks = options.flashTicks ?? ticksPer(this.flashMs, options.tickRateHz);
    this.maxElapsedTicks = options.maxElapsedTicks ?? ticksPer(maxElapsedMs, options.tickRateHz);
  }

  /** The classifier's derived elapsed-tick ceiling — exposed so a caller (or a test) can reason
   * about the forward-seek threshold without recomputing `ticksPer` itself. */
  get elapsedTickCeiling(): number {
    return this.maxElapsedTicks;
  }

  /** Every currently active flash (bee or cell), in no particular order — the caller merges
   * these into its drawable list the same way it reads `CorpseTracker.corpses`. A flash whose
   * presentation window has fully elapsed is omitted rather than returned at zero intensity. */
  get flashes(): DamageFlash[] {
    const result: DamageFlash[] = [];
    for (const [id, flash] of this.activeBees) {
      const intensity = this.intensityOf(flash);
      if (intensity !== null) result.push({ kind: 'bee', id, intensity });
    }
    for (const [id, flash] of this.activeCells) {
      const intensity = this.intensityOf(flash);
      if (intensity !== null) result.push({ kind: 'cell', id, intensity });
    }
    return result;
  }

  /**
   * Consume one accepted close-tier sample frame at sim tick `tick`: classify every bee/cell
   * present both this frame and last frame (a drop clearing the relevant rule spawns/refreshes a
   * flash), then replace the last-seen bookkeeping with this frame's samples — an id absent this
   * frame is dropped from last-seen (bounded memory) and, being absent, is never classified: a
   * vanished bee produces no flash, by construction, not by a special case.
   *
   * A discontinuity token change (`tier`, `connectionEpoch`, `streamEpoch`, or a non-monotonic
   * `playhead`) clears all bookkeeping and every active flash first, exactly like
   * `CorpseTracker`'s reconnect reset — plus the two extra tokens (`tier`, `streamEpoch`) design
   * requires here that `CorpseTracker` does not need.
   */
  onCloseFrame(
    tick: number,
    bees: Iterable<BeeFlashSample>,
    cells: Iterable<CellFlashSample>,
    tokens: DamageFlashTokens,
  ): void {
    if (!this.initialized) {
      // The first frame only adopts a baseline, never resets — otherwise the first accepted
      // frame of every replay/connection would be inert (mirrors `PresentationClock`/
      // `CorpseTracker`'s own first-frame rule).
      this.initialized = true;
      this.adoptTokens(tokens);
    } else if (this.shouldReset(tokens)) {
      this.resetState();
      this.adoptTokens(tokens);
    } else {
      this.adoptTokens(tokens);
    }

    // Drop flashes whose window elapsed at this playhead, so an expired record cannot
    // accumulate between resets — `advance` does the same for the wall-clock ones.
    for (const [id, flash] of this.activeBees) {
      if (flash.spawnTick !== null && this.intensityOf(flash) === null) this.activeBees.delete(id);
    }
    for (const [id, flash] of this.activeCells) {
      if (flash.spawnTick !== null && this.intensityOf(flash) === null) {
        this.activeCells.delete(id);
      }
    }

    const nextLastSeenBees = new Map<number, LastSeenBee>();
    for (const sample of bees) {
      const prev = this.lastSeenBees.get(sample.id);
      nextLastSeenBees.set(sample.id, { tick, health: sample.health });
      if (prev !== undefined && this.classifyBee(prev.tick, prev.health, tick, sample.health)) {
        this.activeBees.set(sample.id, { elapsedMs: 0, spawnTick: tokens.playhead });
      }
    }
    this.lastSeenBees = nextLastSeenBees;

    const nextLastSeenCells = new Map<number, LastSeenCell>();
    for (const sample of cells) {
      const prev = this.lastSeenCells.get(sample.id);
      nextLastSeenCells.set(sample.id, { tick, hpBucket: sample.hpBucket });
      if (
        prev !== undefined &&
        this.classifyCell(prev.tick, prev.hpBucket, tick, sample.hpBucket)
      ) {
        this.activeCells.set(sample.id, { elapsedMs: 0, spawnTick: tokens.playhead });
      }
    }
    this.lastSeenCells = nextLastSeenCells;
  }

  /** Ages every held/fading flash by `elapsedMs` (wall-clock RAF delta), expiring one once its
   * presentation window has fully elapsed. Read every RAF frame, independent of whether a new
   * accepted close frame has arrived (mirrors `CorpseTracker.advance`). A flash classified under
   * a playhead is skipped here — it ages only in `onCloseFrame`, against the playhead, which is
   * what makes a paused replay hold its flashes instead of fading them out under a still
   * playhead. */
  advance(elapsedMs: number): void {
    for (const [id, flash] of this.activeBees) {
      if (flash.spawnTick !== null) continue;
      flash.elapsedMs += elapsedMs;
      if (flash.elapsedMs >= this.flashMs) this.activeBees.delete(id);
    }
    for (const [id, flash] of this.activeCells) {
      if (flash.spawnTick !== null) continue;
      flash.elapsedMs += elapsedMs;
      if (flash.elapsedMs >= this.flashMs) this.activeCells.delete(id);
    }
  }

  /** Clears every tracked flash and last-seen bookkeeping — called by the caller on a tier
   * change in addition to the internal discontinuity reset `onCloseFrame` already performs
   * (mirrors `CorpseTracker.clear`). */
  clear(): void {
    this.resetState();
  }

  /**
   * The bee rule (design E5): a bounded-window rate discriminator, not a raw magnitude test.
   * `elapsed_ticks` beyond the ceiling withholds judgment ("unknown", never a guess). A
   * non-decrease (health rose or held — in-hive regen, or a re-delivered/duplicate sample) never
   * flashes. Otherwise the drop must both clear the absolute floor (`MIN_DROP_UNITS`) and exceed
   * what the fastest-fractional starvation drain could produce over the same window, with a
   * quantization-slack margin — comparing in SECONDS (`elapsed_seconds = elapsed_ticks ÷
   * tickRateHz`) so the bound holds at any tick rate, never just the shipped 15 Hz.
   */
  private classifyBee(prevTick: number, prevHealth: number, tick: number, health: number): boolean {
    const elapsedTicks = tick - prevTick;
    if (elapsedTicks <= 0 || elapsedTicks > this.maxElapsedTicks) return false;
    const dropUnits = prevHealth - health;
    if (dropUnits < MIN_DROP_UNITS) return false;
    const elapsedSeconds = elapsedTicks / this.tickRateHz;
    const starveBound = STARVE_UNITS_PER_SECOND * elapsedSeconds + QUANT_SLACK_UNITS;
    return dropUnits > starveBound;
  }

  /**
   * The cell rule (design E5): no rate discriminator needed. `cell.hp` only ever falls through
   * razing — the only other writer (`repair_cells`) raises and clamps — so ANY bucket decrease
   * within the ceiling window is damage; a rising bucket (a repair) never flashes. The ceiling
   * still applies, purely as a discontinuity guard: beyond it the comparison is against a gap
   * too wide to trust, exactly as for bees.
   */
  private classifyCell(prevTick: number, prevBucket: number, tick: number, bucket: number): boolean {
    const elapsedTicks = tick - prevTick;
    if (elapsedTicks <= 0 || elapsedTicks > this.maxElapsedTicks) return false;
    return bucket < prevBucket;
  }

  /**
   * One active flash's presentation intensity, or `null` once its window has fully elapsed —
   * exactly `CorpseTracker.alphaOf`'s split. A flash classified under a playhead ages in ticks
   * past the tick it was classified on (a held playhead holds it, a scrub does not smear it
   * across wall-clock drag time); one classified in live play (no playhead) ages in wall-clock
   * time via `advance`.
   */
  private intensityOf(flash: ActiveFlash): number | null {
    const playhead = this.lastPlayhead;
    if (flash.spawnTick !== null && playhead !== null) {
      const elapsed = playhead - flash.spawnTick;
      if (elapsed >= this.flashTicks) return null;
      return this.flashTicks <= 0 ? 0 : Math.max(0, 1 - elapsed / this.flashTicks);
    }
    if (flash.elapsedMs >= this.flashMs) return null;
    return this.flashMs <= 0 ? 0 : Math.max(0, 1 - flash.elapsedMs / this.flashMs);
  }

  /**
   * True when any of the four discontinuity tokens changed since the last accepted frame: a
   * `tier` flip, a `connectionEpoch` bump (reconnect), a `streamEpoch` bump (gap or reconnect),
   * or a non-monotonic `playhead` move. The playhead threshold is deliberately the classifier's
   * elapsed-tick CEILING, not the (shorter) flash decay window (design E9): the ceiling is what
   * bounds a meaningful comparison and, being the larger of the two, never fires while the
   * classifier would still have decided — which matters at speed, since 16× playback can advance
   * the playhead several ticks per rendered frame.
   */
  private shouldReset(tokens: DamageFlashTokens): boolean {
    if (tokens.tier !== this.lastTier) return true;
    if (tokens.connectionEpoch !== this.lastConnectionEpoch) return true;
    if (tokens.streamEpoch !== this.lastStreamEpoch) return true;
    return this.playheadBroke(tokens.playhead);
  }

  /** True when `playhead` moved non-contiguously since the last accepted frame — backwards by
   * any amount, forwards by more than the elapsed-tick ceiling, or a live/replay mode flip
   * (`null` ↔ non-`null`), which cannot happen for a live tracker instance but must not be
   * discovered by reading state if it ever did (mirrors `CorpseTracker.playheadBroke`). */
  private playheadBroke(playhead: number | null): boolean {
    const last = this.lastPlayhead;
    if (playhead === null || last === null) return playhead !== last;
    return playhead < last || playhead - last > this.maxElapsedTicks;
  }

  private adoptTokens(tokens: DamageFlashTokens): void {
    this.lastTier = tokens.tier;
    this.lastConnectionEpoch = tokens.connectionEpoch;
    this.lastStreamEpoch = tokens.streamEpoch;
    this.lastPlayhead = tokens.playhead;
  }

  private resetState(): void {
    this.activeBees.clear();
    this.activeCells.clear();
    this.lastSeenBees.clear();
    this.lastSeenCells.clear();
  }
}
